import QuestionRoleType, { QuestionRoleTypeTerm } from "consts/QuestionRoleType";
import { addDays, addMonths, addYears, endOfDay } from "date-fns";

import { DeploymentQuotaType } from "consts/QuestionDeploymentQuotaTypes";
import { QuestionDeploymentQuotaTypes } from "consts";
import { validateIsDateBefore } from "utils/dates";

export type QuestionDeployRulesUser = {
  isSysAdmin: boolean;
};

export type ValidateBeforeSubmitData = {
  quota: number | null;
  endDate: Date | string | null;
};

export const DeploymentDateConstraints = {
  Immediately: "immediately",
  Never: "never",
  StartDate: "start-date",
  EndDate: "end-date",
} as const;

export const DeploymentPacingTypes = {
  Even: "EVEN",
  Asap: "ASAP",
} as const;

const baseQuotaOptions = [
  { value: QuestionDeploymentQuotaTypes.No, label: "No." },
  { value: QuestionDeploymentQuotaTypes.YesCustom, label: "Yes, limit responses to custom quota." },
] as const;

const recurringQuotaOptions = [
  { value: QuestionDeploymentQuotaTypes.YesDaily, label: "Yes, limit responses to daily quota." },
  { value: QuestionDeploymentQuotaTypes.YesWeekly, label: "Yes, limit responses to weekly quota." },
  { value: QuestionDeploymentQuotaTypes.YesMonthly, label: "Yes, limit responses to monthly quota." },
] as const;

/**
 * Captures all of the rules that are different based on user permissions and question type.
 * This allows us to dynamically produce the validation rules for a question deployment.
 */
export type QuestionDeploymentRules = {
  quotaOptions: Readonly<{ value: string; label: string }[]>;
  getQuotaMaxResponses: (quotaType: DeploymentQuotaType) => number;
  validateEndDate: (startDate: Date | null, quotaType: DeploymentQuotaType) => (endDate: Date) => boolean | string;
  /**
   * A final validation check which is provided the QuestionDeployRequest
   * and can perform cross field validation and invariant checks.
   * Returns `null` if `form` is valid or an array of error messages if invalid.
   */
  validateBeforeSubmit: (form: ValidateBeforeSubmitData) => null | string[];
};

/**
 * Returns the deployment rules for a publisher user with an engagement question.
 */
const getQuestionDeployRulesPubEngagment = (): QuestionDeploymentRules => ({
  quotaOptions: [...baseQuotaOptions],
  getQuotaMaxResponses: () => 1_000,
  validateEndDate: (startDate) => (endDate) => {
    return startDate && endDate ? validateIsDateBefore(endDate, endOfDay(addDays(startDate, 8))) : true;
  },
  validateBeforeSubmit: (form) => {
    if (!form.quota && !form.endDate) {
      return ["You must provide either a quota or an end date."];
    }

    return null;
  },
});

/**
 * Returns the deployment rules for a publisher user with a value question.
 */
export const getQuestionDeployRulesPubValue = (): QuestionDeploymentRules => ({
  quotaOptions: [...baseQuotaOptions, ...recurringQuotaOptions],
  getQuotaMaxResponses: (quotaType) => {
    switch (quotaType) {
      case QuestionDeploymentQuotaTypes.No:
      case QuestionDeploymentQuotaTypes.YesCustom:
        return 3_000;
      case QuestionDeploymentQuotaTypes.YesDaily:
        return 100;
      case QuestionDeploymentQuotaTypes.YesWeekly:
        return 700;
      case QuestionDeploymentQuotaTypes.YesMonthly:
        return 3_000;
    }
  },
  validateEndDate: (startDate, quotaType) => (endDate) => {
    if (!startDate || !endDate) {
      return true;
    }

    const maxEndDate =
      quotaType === QuestionDeploymentQuotaTypes.No || quotaType === QuestionDeploymentQuotaTypes.YesCustom
        ? addMonths(startDate, 1)
        : addYears(startDate, 1);

    return validateIsDateBefore(endDate, endOfDay(maxEndDate));
  },
  validateBeforeSubmit: () => null,
});

/**
 * Returns the default rules for question deployment.
 * These are the least restrictive rules which sys admins use
 * and publishers use for certain question types.
 */
const getQuestionDeployRulesDefault = (): QuestionDeploymentRules => ({
  quotaOptions: [...baseQuotaOptions, ...recurringQuotaOptions],
  getQuotaMaxResponses: () => 10_000_000,
  validateEndDate: () => () => true,
  validateBeforeSubmit: () => null,
});

type GetQuestionDeployRulesArgs = {
  user: QuestionDeployRulesUser;
  questioRoleType: QuestionRoleTypeTerm;
};

/**
 * Returns the question deployment rules based on the provided user.
 *
 * NOTE: This uses `isSysAdmin` which functionally works and matches requirements.
 * However, we may want to consider a role/permission check in the future.
 */
export const getQuestionDeployRules = ({
  user,
  questioRoleType,
}: GetQuestionDeployRulesArgs): QuestionDeploymentRules => {
  if (user.isSysAdmin) {
    return getQuestionDeployRulesDefault();
  }

  if (questioRoleType === QuestionRoleType.Value) {
    return getQuestionDeployRulesPubValue();
  }

  if (questioRoleType === QuestionRoleType.Engagement) {
    return getQuestionDeployRulesPubEngagment();
  }

  return getQuestionDeployRulesDefault();
};
